import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChallengeService } from '@duck/api-definitions'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { HelpCircle, Send, Settings } from 'lucide-react'
import { RelativeDateTime } from '@components/datetime'
import { LastWorkSave } from './LastWorkSave'
import { WorkspaceTourDialog } from './WorkspaceTourDialog'
import { useTar } from '@/modules'
import { useUserSettings } from '@/hooks/useUserSettings'
import { useSubmitToReview } from '@/hooks/useSubmitToReview'
import { useOrgBranding } from '@/hooks/useOrgBranding'
import { formatWorkTime } from '@/utils/time.utils'
import { useSetAtom } from 'jotai'
import { workspaceSettingsOpenAtom } from '@/store/workspaceAtoms'
import { trackWorkspaceSettings } from '@/modules/workspaceSettingsAnalytics'
import { OrgAvatar } from '@/components/OrgAvatar'

interface InformationBarProps {
  challenge: ChallengeService.Challenge;
  task: ChallengeService.Task;
  workTime: number;
  /** When true, show submit button instead of help (used on mobile) */
  showSubmitButton?: boolean;
}

export const WorkspaceHeaderPane = ({
  challenge,
  task,
  workTime,
  showSubmitButton = false,
}: InformationBarProps) => {
  const { t } = useTar()
  const navigate = useNavigate()
  const { getSetting, setSetting } = useUserSettings()
  const { handleSubmit } = useSubmitToReview()
  const { branding: organization } = useOrgBranding(challenge.organizationId)
  const setSettingsOpen = useSetAtom(workspaceSettingsOpenAtom)
  const openSettings = () => {
    trackWorkspaceSettings('workspace_settings:open', {
      source: 'header_button',
      challengeId: challenge.id,
      taskId: task.id,
    })
    setSettingsOpen(true)
  }
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false)
  const [isFiveMinuteDialogOpen, setIsFiveMinuteDialogOpen] = useState(false)
  const [isTimeUpDialogOpen, setIsTimeUpDialogOpen] = useState(false)
  const openedAutomaticallyRef = useRef(false)

  useEffect(() => {
    if (getSetting('dismissedRodeoWorkspaceVideo') !== true) {
      setIsHelpDialogOpen(true)
      openedAutomaticallyRef.current = true
    }
  }, [getSetting])

  useEffect(() => {
    if (!challenge.submittableUntil) {
      return
    }

    const deadlineMs = new Date(challenge.submittableUntil).getTime()
    if (Number.isNaN(deadlineMs)) {
      return
    }

    const triggerAt = deadlineMs - 5 * 60 * 1000
    const now = Date.now()
    const delay = triggerAt - now

    if (delay <= 0) {
      setIsFiveMinuteDialogOpen(true)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setIsFiveMinuteDialogOpen(true)
    }, delay)

    return () => window.clearTimeout(timeoutId)
  }, [challenge.submittableUntil, setIsFiveMinuteDialogOpen])

  useEffect(() => {
    if (!challenge.submittableUntil) {
      return
    }

    const deadlineMs = new Date(challenge.submittableUntil).getTime()
    if (Number.isNaN(deadlineMs)) {
      return
    }

    const now = Date.now()
    const delay = deadlineMs - now

    if (delay <= 0) {
      setIsTimeUpDialogOpen(true)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setIsTimeUpDialogOpen(true)
    }, delay)

    return () => window.clearTimeout(timeoutId)
  }, [challenge.submittableUntil])

  const handleTimeUpDialogClose = (open: boolean) => {
    if (!open) {
      navigate({ to: '/' })
    }
    setIsTimeUpDialogOpen(open)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && openedAutomaticallyRef.current) {
      setSetting('dismissedRodeoWorkspaceVideo', true)
      openedAutomaticallyRef.current = false
    }
    setIsHelpDialogOpen(open)
  }

  const openFromHelpButton = () => {
    openedAutomaticallyRef.current = false
    setIsHelpDialogOpen(true)
  }

  return (
    <div className="flex w-full pt-2 pb-1 items-center justify-center min-h-12">
      <div className="flex flex-1 items-center justify-between md:ps-4 md:pe-2">
        <div className="flex items-center gap-2" data-tour-id="task-name">
          {organization?.logoSquareURL && (
            <OrgAvatar size="sm" org={organization} />
          )}
          <span className="font-semibold text-foreground">{task.name}</span>
        </div>

        <div className="flex items-stretch">
          <div className="flex items-stretch divide-x rtl:divide-x-reverse divide-border">
            {!challenge.submittableUntil && (
              <div className="hidden md:flex flex-col justify-center gap-1.5 px-4">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground leading-none">
                  {t('common.workTime')}
                </span>
                <span className="text-sm font-semibold text-foreground font-mono tabular-nums leading-none">
                  {formatWorkTime(workTime, t, { short: true })}
                </span>
              </div>
            )}
            {!challenge.submittableUntil && (
              <div className="hidden lg:flex flex-col justify-center gap-1.5 px-4">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground leading-none">
                  {t('common.started')}
                </span>
                <RelativeDateTime
                  isoDateString={challenge.startTime}
                  className="text-sm font-medium text-foreground leading-none"
                  noYear
                />
              </div>
            )}
            {challenge.submittableUntil && (
              <div className="hidden xl:flex flex-col justify-center gap-1.5 px-4">
                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground leading-none">
                  {t('common.remaining')}
                </span>
                <RelativeDateTime
                  isoDateString={challenge.submittableUntil}
                  className="text-sm font-semibold text-foreground font-mono tabular-nums leading-none"
                  onlyDuration
                />
              </div>
            )}
            <LastWorkSave />
          </div>

          <div className="flex items-center gap-1 ps-2">
            {showSubmitButton ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 shrink-0 px-1"
                onClick={handleSubmit}
                aria-label={t('common.submit')}
                data-tour-id="submit-solution"
              >
                <Send className="size-5" />
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 shrink-0"
                  onClick={openFromHelpButton}
                  aria-label={t('common.help')}
                >
                  <HelpCircle className="size-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 shrink-0"
                  onClick={openSettings}
                  aria-label={t('common.settings')}
                  data-testid="workspace-settings-open"
                >
                  <Settings className="size-5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <WorkspaceTourDialog
        open={isHelpDialogOpen}
        onOpenChange={handleOpenChange}
      />
      <Dialog
        open={isFiveMinuteDialogOpen}
        onOpenChange={setIsFiveMinuteDialogOpen}
      >
        <DialogContent noMinHeight noCloseButton width="xs">
          <DialogHeader>
            <DialogTitle className="text-center">
              {t('common.youHaveXMinutesToSubmitYourSolution', {
                minutes: challenge.submittableUntil
                  ? Math.max(
                      0,
                      Math.ceil(
                        (new Date(challenge.submittableUntil).getTime() -
                          Date.now()) /
                          (60 * 1000)
                      )
                    )
                  : 5,
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => setIsFiveMinuteDialogOpen(false)}>
              {t('common.ok')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isTimeUpDialogOpen} onOpenChange={handleTimeUpDialogClose}>
        <DialogContent noMinHeight noCloseButton>
          <DialogHeader>
            <DialogTitle>
              {t('common.timeIsUpSubmissionIsNowClosed')}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => handleTimeUpDialogClose(false)}>
              {t('common.goHome')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
